/**
 * `useSystemHealth()` — the one place the web app turns REAL operational
 * signals into a health summary (project decision D-11: no invented rows).
 * Consumed by the dashboard's System Health card and the sidebar footer.
 *
 * Sources:
 *  - `GET /health/ready` (the prefixed alias of the public `/readyz`
 *    readiness probe — `/readyz` itself sits outside the `/api/v1` prefix
 *    and is not reachable through the SPA's same-origin `/api` proxy):
 *    can the API reach Postgres? A 503 from it means "degraded".
 *  - the admin tier (`systemHealth`) of `GET /reports/summary`, which the
 *    server only returns to `report:read:any` holders (ADMIN, AUDITOR):
 *    the outbox (background delivery worker) and the inbound email review
 *    queues. Read through the SAME react-query key the dashboard uses, so
 *    a page that shows both never issues a second request, and it is not
 *    requested at all for anyone who couldn't receive the tier.
 *
 * Non-admins only ever see an overall status plus a single "Service" row —
 * never the infrastructure breakdown.
 */
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS, type ReportSystemHealth } from '@ithelpdesk/shared';
import { ApiError, apiGet } from './client';
import { useReportSummary } from './reports';
import { usePermission } from './session';

export type SystemHealthStatus = 'operational' | 'degraded' | 'down' | 'unknown';
export type SystemHealthRowState = 'healthy' | 'warning' | 'failed';

export interface SystemHealthRow {
  key: string;
  label: string;
  state: SystemHealthRowState;
  detail?: string;
}

export interface SystemHealth {
  status: SystemHealthStatus;
  rows: SystemHealthRow[];
  checkedAt: number | null;
  isLoading: boolean;
  /**
   * What the status actually covers: `full` = the admin tier (queues, email,
   * database); `service` = API/database readiness only (everyone else). Copy
   * must not claim "all systems" for a `service`-scope check (D-11).
   */
  scope: 'full' | 'service';
}

interface ReadinessResponse {
  status: 'ready' | 'degraded';
  checks: Record<string, { ok: boolean; detail?: string }>;
}

/** A backlog the worker normally drains in seconds; past this it's worth a look. */
export const OUTBOX_BACKLOG_WARNING = 50;

const READINESS_POLL_MS = 60_000;

export const systemHealthQueryKeys = {
  readiness: () => ['health', 'ready'] as const,
};

/**
 * Readiness as data: a 503 is a real answer ("degraded"), not a failed
 * request, so it resolves instead of throwing. Anything else (network down,
 * 5xx from a proxy) stays an error → status `unknown`.
 */
async function fetchReadiness(signal: AbortSignal): Promise<ReadinessResponse> {
  try {
    return await apiGet<ReadinessResponse>('/health/ready', { signal });
  } catch (error) {
    if (error instanceof ApiError && error.status === 503) {
      return { status: 'degraded', checks: { database: { ok: false } } };
    }
    throw error;
  }
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Maps the admin-tier signals + readiness onto rows. Exported for tests. */
export function adminHealthRows(h: ReportSystemHealth, databaseOk: boolean): SystemHealthRow[] {
  const inbound = h.emailNeedsReview + h.emailQuarantined;
  const jobsState: SystemHealthRowState = h.outboxFailed > 0 ? 'failed' : h.outboxBacklog > OUTBOX_BACKLOG_WARNING ? 'warning' : 'healthy';
  return [
    {
      key: 'email',
      label: 'Email Processing',
      state: inbound > 0 ? 'warning' : 'healthy',
      detail: inbound > 0 ? `${h.emailNeedsReview} awaiting review · ${h.emailQuarantined} quarantined` : 'Inbound review queue is clear',
    },
    {
      key: 'jobs',
      label: 'Background Jobs',
      state: jobsState,
      detail:
        h.outboxFailed > 0
          ? `${plural(h.outboxFailed, 'delivery', 'deliveries')} failed · ${h.outboxBacklog} queued`
          : `${h.outboxBacklog} queued for the delivery worker`,
    },
    {
      key: 'database',
      label: 'Database',
      state: databaseOk ? 'healthy' : 'failed',
      detail: databaseOk ? 'Reachable' : 'Unreachable',
    },
  ];
}

export function overallStatus(rows: SystemHealthRow[]): SystemHealthStatus {
  if (rows.length === 0) return 'unknown';
  if (rows.some((r) => r.key === 'database' && r.state === 'failed') || rows.some((r) => r.key === 'service' && r.state === 'failed')) {
    return 'down';
  }
  if (rows.some((r) => r.state !== 'healthy')) return 'degraded';
  return 'operational';
}

export function useSystemHealth(): SystemHealth {
  // Same gate the server uses to populate the admin tier. UX only — the
  // server omits the tier for everyone else regardless.
  const canSeeAdminTier = usePermission(PERMISSIONS.REPORT_READ_ANY);
  const summary = useReportSummary({ enabled: canSeeAdminTier });
  const readiness = useQuery({
    queryKey: systemHealthQueryKeys.readiness(),
    queryFn: ({ signal }) => fetchReadiness(signal),
    staleTime: 30_000,
    refetchInterval: READINESS_POLL_MS,
    retry: 1,
  });

  const readyLoading = readiness.isLoading;
  const summaryLoading = canSeeAdminTier && summary.isLoading;
  const isLoading = readyLoading || summaryLoading;

  if (isLoading || readiness.isError || !readiness.data) {
    return { status: 'unknown', rows: [], checkedAt: null, isLoading, scope: canSeeAdminTier ? 'full' : 'service' };
  }

  const databaseOk = readiness.data.status === 'ready' && readiness.data.checks.database?.ok !== false;

  if (!canSeeAdminTier) {
    const rows: SystemHealthRow[] = [{ key: 'service', label: 'Service', state: databaseOk ? 'healthy' : 'failed' }];
    return { status: overallStatus(rows), rows, checkedAt: readiness.dataUpdatedAt || null, isLoading: false, scope: 'service' };
  }

  const tier = summary.data?.admin?.systemHealth;
  if (summary.isError || !tier) {
    // We can't vouch for the queues, so don't claim "operational".
    return { status: 'unknown', rows: [], checkedAt: null, isLoading: false, scope: 'full' };
  }

  const rows = adminHealthRows(tier, databaseOk);
  const stamps = [readiness.dataUpdatedAt, summary.dataUpdatedAt].filter((n) => n > 0);
  return { status: overallStatus(rows), rows, checkedAt: stamps.length ? Math.min(...stamps) : null, isLoading: false, scope: 'full' };
}
